import { createSignal, JSX, Show, children, Component, createEffect, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import '../css/tooltips.css';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: string | JSX.Element;
  position?: TooltipPosition;
  children: JSX.Element;
  delay?: number; // 延迟显示时间（毫秒）
  disabled?: boolean;
  class?: string;
  contentClass?: string;
  onlyOverflow?: boolean; // 仅当内容溢出时才显示 tooltip
}

export const Tooltip: Component<TooltipProps> = (props) => {
  const [isVisible, setIsVisible] = createSignal(false);
  const [tooltipPosition, setTooltipPosition] = createSignal({ top: 0, left: 0 });
  let timeoutId: number | undefined;
  let tooltipRef: HTMLDivElement | undefined;
  let containerRef: HTMLDivElement | undefined;

  const position = () => props.position || 'top';
  const delay = () => props.delay ?? 200;

  const calculatePosition = () => {
    if (!containerRef || !tooltipRef) return;

    const containerRect = containerRef.getBoundingClientRect();
    const tooltipRect = tooltipRef.getBoundingClientRect();
    const pos = position();
    const spacing = 8; // 与箭头的间距

    let top = 0;
    let left = 0;

    switch (pos) {
      case 'top':
        top = containerRect.top - tooltipRect.height - spacing;
        left = containerRect.left + containerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = containerRect.bottom + spacing;
        left = containerRect.left + containerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = containerRect.top + containerRect.height / 2 - tooltipRect.height / 2;
        left = containerRect.left - tooltipRect.width - spacing;
        break;
      case 'right':
        top = containerRect.top + containerRect.height / 2 - tooltipRect.height / 2;
        left = containerRect.right + spacing;
        break;
    }

    // 确保 tooltip 不会超出视口
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 横向边界检查
    if (left < 0) {
      left = spacing;
    } else if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - spacing;
    }

    // 纵向边界检查
    if (top < 0) {
      top = spacing;
    } else if (top + tooltipRect.height > viewportHeight) {
      top = viewportHeight - tooltipRect.height - spacing;
    }

    setTooltipPosition({ top, left });
  };

  const handleMouseEnter = () => {
    if (props.disabled) return;

    // 如果设置了 onlyOverflow 且内容没有溢出，则不显示 tooltip
    if (props.onlyOverflow && containerRef) {
      // 获取第一个子元素进行判断（如果有的话）
      const contentEl = containerRef.firstElementChild || containerRef;
      if (contentEl.scrollWidth <= contentEl.clientWidth) {
        return;
      }
    }

    timeoutId = window.setTimeout(() => {
      setIsVisible(true);
    }, delay());
  };

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setIsVisible(false);
  };

  // 当 tooltip 显示时，计算位置
  createEffect(() => {
    if (isVisible()) {
      // 需要等待下一帧，确保 DOM 已更新
      requestAnimationFrame(() => {
        calculatePosition();
      });

      // 监听滚动和窗口大小变化，重新计算位置
      const handleUpdate = () => {
        if (isVisible()) {
          calculatePosition();
        }
      };

      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);

      onCleanup(() => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      });
    }
  });

  const resolvedChildren = children(() => props.children);

  return (
    <>
      <div
        ref={containerRef}
        class={cx('tooltip-container', props.class)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {resolvedChildren()}
      </div>
      <Show when={isVisible() && !props.disabled}>
        <Portal>
          <div
            ref={tooltipRef}
            class={`tooltip-portal tooltip-${position()} ${props.contentClass}`}
            role="tooltip"
            style={{
              position: 'fixed',
              top: `${tooltipPosition().top}px`,
              left: `${tooltipPosition().left}px`,
              'z-index': '19999',
            }}
          >
            <div class={cx('tooltip-content', props.contentClass)}>{props.content}</div>
            <div class="tooltip-arrow" />
          </div>
        </Portal>
      </Show>
    </>
  );
};
